import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-hope",
  title: "A prayer for hope — when you have stopped expecting anything",
  h1: "A prayer for hope",
  description:
    "A prayer for the flat stretch after hoping stopped working, four passages, and why biblical hope is nothing like optimism.",
  targets: [
    "prayer for hope",
    "prayer when you feel hopeless",
    "prayer for hope in hard times",
    "prayer when nothing is getting better",
  ],
  published: "2026-09-03",
  standfirst:
    "Hope in the New Testament is not a mood about the future. It is a claim about who is at the other end of it, which is why it survives evidence.",
  takeaways: [
    "Hope in these passages is anchored to a person rather than to an outcome, which is why it can hold when the outcome goes.",
    "Romans 5:3–5 runs suffering to perseverance to character to hope — hope arrives last, and after the hard part rather than instead of it.",
    "If nothing has felt possible for months, that flatness may be a medical thing. It responds to treatment; it does not respond to being told to cheer up.",
  ],
  when: [
    "When the thing you were waiting for has fallen through for the third time.",
    "In the flat stretch after a loss, when everyone assumes you are through it.",
    "When you have stopped praying about something because you got tired of being disappointed.",
    "When the news has been the same news for two years.",
  ],
  prayer: [
    "God, I have stopped expecting things, and I did it quietly enough that nobody noticed, including me for a while.",
    "It was not a decision. It was a way of not being disappointed again, and it has worked, and it has cost more than the disappointments did.",
    "I am not asking to feel optimistic. I have no evidence for optimism and I am not going to manufacture some.",
    "I am asking for the other kind — the kind that is about you rather than about the odds. Give me enough of it to keep asking.",
    "And let me not perform being fine. Not with you, and not with the people who could actually help.",
  ],
  short: "God, not optimism. The other kind — enough to keep asking.",
  honest: [
    "This prayer does not promise the situation turns. Hope in these passages is anchored to a person rather than to an outcome precisely because outcomes go both ways, and pretending otherwise makes the whole thing brittle.",
    "It also does not require you to be positive. Nearly a third of the psalms are complaints and several end unresolved; a tradition that keeps those in its prayer book is not asking anybody to be upbeat.",
    "And there is a flatness that is not despair but depression — months of nothing mattering, in every area at once. That is treatable, it responds to help, and no amount of praying about hope is the right first response to it.",
  ],
  verses: [
    {
      verse: {
        text: "Not only this, but we also rejoice in our sufferings, knowing that suffering produces perseverance; and perseverance, proven character; and proven character, hope; and hope doesn't disappoint us, because God's love has been poured into our hearts through the Holy Spirit who was given to us.",
        ref: "Romans 5:3–5",
      },
      note: "A chain with hope at the end rather than the beginning, which is the opposite of how the word is usually used. Nothing here suggests skipping the first link. And the reason hope does not disappoint is not that things work out — it is a love already poured in, in the past tense, before any of the outcomes are known.",
    },
    {
      verse: {
        text: "This hope we have as an anchor of the soul, a hope both sure and steadfast and entering into that which is within the veil,",
        ref: "Hebrews 6:19",
      },
      note: "An anchor is not an engine. It does not move you anywhere and it does not improve the weather; it stops you being carried off while the weather does what it does. The image also puts the anchor somewhere else — inside the veil, out of sight — which is the whole difference from optimism, whose anchor is the forecast.",
    },
    {
      verse: {
        text: "I wait for Yahweh. My soul waits. I hope in his word.",
        ref: "Psalm 130:5",
      },
      note: "Three short clauses out of the depths, which is where the psalm opens. The hope is placed in a word rather than in a change of circumstances, and the psalm never reports the circumstances changing. What it reports is somebody still there in the morning, which is the psalm's idea of an answer.",
    },
    {
      verse: {
        text: "Now may the God of hope fill you with all joy and peace in believing, that you may abound in hope in the power of the Holy Spirit.",
        ref: "Romans 15:13",
      },
      note: "A blessing rather than an instruction, which matters on a day when you cannot produce any of this. Nobody in the sentence is being told to be hopeful; hope is described as something poured in from elsewhere. If you have none, this verse is not asking you to find some.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "It is not optimism",
      id: "it-is-not-optimism",
    },
    {
      type: "p",
      text: "Optimism is a forecast: things will probably be fine. It is falsifiable, and a couple of bad years falsify it. What the New Testament calls hope is not a prediction about outcomes at all — it is confidence about who is there, which is why the writers use it while describing prison, shipwreck and execution.",
    },
    {
      type: "p",
      text: "That distinction is not a consolation prize. It is the reason this kind of hope can survive an outcome going the wrong way, and the optimistic kind cannot.",
    },
    {
      type: "h2",
      text: "You stopped hoping on purpose",
      id: "you-stopped-hoping-on-purpose",
    },
    {
      type: "p",
      text: "Most people who have lost hope did not lose it. They put it down, deliberately, because hoping had become the most painful part — each new possibility carrying the cost of another disappointment.",
    },
    {
      type: "note",
      text: "That is a rational strategy and it works. It is also why coming back is not a matter of trying harder; it is a matter of deciding the cost is worth paying again.",
    },
    {
      type: "h2",
      text: "Enough to keep asking",
      id: "enough-to-keep-asking",
    },
    {
      type: "p",
      text: "The measure to aim for is not feeling hopeful. It is continuing to ask — showing up to the same request without the expectation that used to power it, which is what the psalms of lament are, structurally.",
    },
    {
      type: "p",
      text: "And if even that has gone, the honest place to start is the one for when the words have stopped entirely: {say there is nothing, and stay a minute}.",
      links: {
        "say there is nothing, and stay a minute":
          "/prayers/prayer-when-you-cant-pray",
      },
    },
  ],
  faq: [
    {
      q: "What is a good prayer when you feel hopeless?",
      a: '"God, not optimism — the other kind, enough to keep asking." Biblical hope is anchored to a person rather than to a forecast, which is why it does not require you to believe the odds have improved.',
    },
    {
      q: "Which Bible verse is about hope?",
      a: "Romans 15:13 is a blessing rather than an instruction, which helps on a day when you cannot produce any. Hebrews 6:19 gives the image: an anchor, which does not move you anywhere but stops you being carried off.",
    },
    {
      q: "What is the difference between hope and optimism?",
      a: "Optimism is a prediction about outcomes and can be disproved by two bad years. New Testament hope is confidence about who is at the other end, which is why the writers use the word while describing prison and shipwreck.",
    },
  ],
  relatedPrayers: [
    "prayer-when-you-cant-pray",
    "prayer-for-strength",
    "prayer-for-grief",
  ],
  related: ["phone-addiction-bible-verses"],
  relatedTopics: [
    "bible-verses-about-grief",
    "bible-verses-about-strength",
    "bible-verses-about-hope",
  ],
  relatedPassages: ["jeremiah-29-11", "isaiah-41-10"],
};
